<div class="flex flex-column">
  <!-- Header: date + greeting -->
  <div class="flex flex-column">
    <div class="text-xs-semibold text-gray-500">{{ todayLabel() }}</div>
    <div class="dashboard-greeting dashboard-title display-md-semibold text-gray-900">Dobrodošli, {{ greetingName() }}!</div>
    <div class="dashboard-subtitle text-md-regular text-gray-600">Pregled vašeg naloga i dostupnih sati</div>
  </div>

  <!-- Onboarding banner (no active package) -->
  @if (showBanner()) {
    <div class="banner banner--dark banner--dismissible mt-4">
      <div class="flex align-items-center gap-3 banner__content">
        <div class="banner__icon">
          <app-cube-01 stroke="#F0F2F5" width="20px" height="20px"></app-cube-01>
        </div>
        <div class="flex flex-column gap-1">
          <div class="text-sm-semibold banner__title">Sledeći korak: kupovina paketa</div>
          <div class="text-sm-regular banner__sub">
            Zakazivanje termina biće dostupno nakon kupovine paketa
          </div>
        </div>
      </div>
      <svg class="banner__arc" viewBox="0 0 280 140" fill="none" aria-hidden="true">
        <circle cx="240" cy="70" r="160" stroke="rgba(255,255,255,0.12)" stroke-width="48" />
        <circle cx="240" cy="70" r="96" stroke="rgba(255,255,255,0.08)" stroke-width="32" />
      </svg>
      <div class="banner__actions flex align-items-center gap-2">
        <app-button
          [btnSize]="ButtonSize.MD"
          [btnSeverity]="ButtonSeverity.SECONDARY"
          (btnClicked)="goToOffer()"
        >
          <app-cube-01 stroke="#181D27" width="20px" height="20px"></app-cube-01>
          <span class="text-sm-semibold text-gray-900">Kupite paket</span>
        </app-button>
      </div>
      <button class="banner__close" (click)="dismissBanner()" aria-label="Zatvori">
        <app-x-close stroke="#FFFFFF" width="14" height="14"></app-x-close>
      </button>
    </div>
  } @else if (showProcessingBanner()) {
    <div class="banner banner--dark mt-4">
      <div class="flex align-items-center gap-3 banner__content">
        <div class="banner__icon">
          <app-clock-fast-forward stroke="#F79009" width="20px" height="20px"></app-clock-fast-forward>
        </div>
        <div class="flex flex-column gap-1">
          <div class="text-sm-semibold banner__title">Vaša porudžbina je u obradi.</div>
          <div class="text-sm-regular banner__sub">
            Status možete proveriti u tabu &ldquo;U obradi&rdquo; u okviru sekcije Svi paketi.
          </div>
        </div>
      </div>
      <svg class="banner__arc" viewBox="0 0 280 140" fill="none" aria-hidden="true">
        <circle cx="240" cy="70" r="160" stroke="rgba(255,255,255,0.12)" stroke-width="48"/>
        <circle cx="240" cy="70" r="96" stroke="rgba(255,255,255,0.08)" stroke-width="32"/>
      </svg>
    </div>
  }

  <!-- Pregled -->
  <div class="section-card mt-card">
    <div class="text-lg-semibold text-gray-900">Pregled</div>

    <div class="stats-grid mt-card">
      <!-- Preostali sati -->
      <div class="stat-tile">
        <div class="stat-tile__content">
          <div class="stat-tile__head flex align-items-center">
            <div class="stat-tile__icon">
              <app-hourglas-03 stroke="#181D27" width="20px" height="20px"></app-hourglas-03>
            </div>
            <div class="stat-tile__title text-sm-semibold text-gray-600">Preostali sati</div>
          </div>
          <div class="stat-tile__value">
            @if (dashLoading()) {
              <p-skeleton width="6rem" height="2.375rem"></p-skeleton>
            } @else if ((dash()?.totalHours ?? 0) === 0) {
              <!-- No packages: "-" like the Paketi page (Figma) -->
              <span class="display-sm-semibold text-gray-900">-</span>
            } @else {
              <span class="display-sm-semibold text-gray-900">{{ dash()?.availableHours ?? 0 }}&nbsp;h</span>
              <span class="stat-tile__unit display-sm-semibold text-gray-400">/ {{ dash()?.totalHours ?? 0 }}&nbsp;h</span>
            }
          </div>
        </div>
        <div class="stat-tile__footer">
          <span class="text-sm-medium text-gray-500">{{ packageCountLabel(dash()?.activePackageCount) }}</span>
          @if ((dash()?.activePackageCount ?? 0) > 0) {
            <a class="stat-link tap-target text-sm-semibold" (click)="goToPackages()">
              <span>Moji paketi</span>
              <app-chevron-right stroke="#2970FF" width="5px" height="10px"></app-chevron-right>
            </a>
          }
        </div>
      </div>

      <!-- Termini u tekućem mesecu -->
      <div class="stat-tile">
        <div class="stat-tile__content">
          <div class="stat-tile__head flex align-items-center">
            <div class="stat-tile__icon">
              <app-list stroke="#181D27" width="20px" height="16px"></app-list>
            </div>
            <div class="stat-tile__title text-sm-semibold text-gray-600">Termini u tekućem mesecu</div>
          </div>
          <div class="stat-tile__value">
            @if (dashLoading()) {
              <p-skeleton width="6rem" height="2.375rem"></p-skeleton>
            } @else {
              <span class="display-sm-semibold text-gray-900">{{ dash()?.appointmentsThisMonth ?? 0 }}</span>
              <span class="stat-tile__unit display-sm-semibold text-gray-400">{{ (dash()?.appointmentsThisMonth ?? 0) === 1 ? 'termin' : 'termina' }}</span>
            }
          </div>
        </div>
        <div class="stat-tile__footer">
          <span class="text-sm-medium text-gray-500">{{ currentMonthLabel() }}</span>
          @if ((dash()?.appointmentsThisMonth ?? 0) > 0) {
            <a class="stat-link tap-target text-sm-semibold" (click)="goToAppointments()">
              <span>Svi termini</span>
              <app-chevron-right stroke="#2970FF" width="5px" height="10px"></app-chevron-right>
            </a>
          }
        </div>
      </div>

      <!-- Sledeći termin za -->
      <div class="stat-tile">
        <div class="stat-tile__content">
          <div class="stat-tile__head flex align-items-center">
            <div class="stat-tile__icon">
              <app-clock stroke="#181D27" width="20px" height="20px"></app-clock>
            </div>
            <div class="stat-tile__title text-sm-semibold text-gray-600">Sledeći termin za</div>
          </div>
          <div class="stat-tile__value">
            @if (dashLoading()) {
              <p-skeleton width="6rem" height="2.375rem"></p-skeleton>
            } @else {
              <span class="display-sm-semibold text-gray-900">{{ daysUntilLabel(dash()?.daysUntilNextAppointment) }}</span>
              @if (daysUntilUnit(dash()?.daysUntilNextAppointment)) {
                <span class="stat-tile__unit display-sm-semibold text-gray-400">{{ daysUntilUnit(dash()?.daysUntilNextAppointment) }}</span>
              }
            }
          </div>
        </div>
        <div class="stat-tile__footer">
          <span class="text-sm-medium text-gray-500">{{ nextAppointmentDateLabel(dash()?.nextAppointmentDateTime) }}</span>
          @if (dash()?.nextAppointmentDateTime) {
            <a class="stat-link tap-target text-sm-semibold" (click)="goToAppointments()">
              <span>Vidi detalje</span>
              <app-chevron-right stroke="#2970FF" width="5px" height="10px"></app-chevron-right>
            </a>
          }
        </div>
      </div>
    </div>
  </div>

  <!-- Quick action cards -->
  <div class="actions-grid mt-card">
    <button class="action-card" (click)="goToOffer()">
      <div class="flex align-items-start justify-content-between">
        <app-cube-01 stroke="#181D27" width="20px" height="20px"></app-cube-01>
        <app-arrow-up-right stroke="#181D27" width="10px" height="10px"></app-arrow-up-right>
      </div>
      <div class="action-card__title text-md-semibold text-gray-900">Kupite paket</div>
      <div class="action-card__subtitle text-sm-regular text-gray-600">Izaberite odgovarajući paket</div>
    </button>

    <button
      class="action-card"
      [class.action-card--disabled]="!canBook()"
      [disabled]="!canBook()"
      (click)="goToCreateAppointment()"
    >
      <div class="flex align-items-start justify-content-between">
        <app-clock stroke="#181D27" width="20px" height="20px"></app-clock>
        <app-arrow-up-right stroke="#181D27" width="10px" height="10px"></app-arrow-up-right>
      </div>
      <div class="action-card__title text-md-semibold text-gray-900">Zakažite termin</div>
      <div class="action-card__subtitle text-sm-regular text-gray-600">Izaberite vreme projekcije</div>
      @if (!canBook()) {
        <div class="action-card__hint text-sm-medium text-gray-700">Dostupno nakon kupovine paketa</div>
      }
    </button>

    <button class="action-card" (click)="goToCalendar()">
      <div class="flex align-items-start justify-content-between">
        <app-calendar stroke="#181D27" width="20px" height="20px"></app-calendar>
        <app-arrow-up-right stroke="#181D27" width="10px" height="10px"></app-arrow-up-right>
      </div>
      <div class="action-card__title text-md-semibold text-gray-900">Pogledajte kalendar</div>
      <div class="action-card__subtitle text-sm-regular text-gray-600">Pregled dostupnosti termina</div>
    </button>
  </div>

  <!-- Predstojeći termin -->
  <div class="upcoming-card mt-card">
    <div class="upcoming-card__header flex align-items-center justify-content-between">
      <div class="text-lg-semibold text-gray-900">Predstojeći termin</div>
      @if (!apptsLoading() && appointments().length > 0) {
        <a class="stat-link tap-target text-sm-semibold" (click)="goToAppointments()">
          <span>Vidi sve</span>
          <app-chevron-right stroke="#2970FF" width="5px" height="10px"></app-chevron-right>
        </a>
      }
    </div>

    <div class="upcoming-card__content">
      @if (apptsLoading()) {
        <div class="flex flex-column gap-3">
          @for (row of [1, 2]; track row) {
            <div class="appt-row flex align-items-center gap-3">
              <p-skeleton width="3rem" height="2.75rem"></p-skeleton>
              <div class="flex flex-column gap-2">
                <p-skeleton width="7rem"></p-skeleton>
                <p-skeleton width="4rem" height="1.25rem"></p-skeleton>
              </div>
            </div>
          }
        </div>
      } @else if (appointments().length === 0) {
        <div class="empty-state flex flex-column align-items-center justify-content-center">
          <div class="empty-state__icon">
            <app-info-circle stroke="#ADB0B8" width="20px" height="20px"></app-info-circle>
          </div>
          <div class="empty-state__text text-md-semibold text-gray-900">Nemate zakazanih termina</div>
          <div class="text-sm-regular text-gray-600 mt-1">
            Ovde će se prikazati vaši predstojeći termini kada budu zakazani.
          </div>
        </div>
      } @else {
        <div class="flex flex-column gap-3">
          @for (appt of appointments(); track appt.id) {
            <div
              class="appt-row flex align-items-center justify-content-between gap-3 cursor-pointer"
              (click)="openDetails(appt.id!)"
            >
              <div class="flex align-items-center gap-3">
                <div class="date-tile flex flex-column align-items-center">
                  <div class="date-tile-month">{{ getMonthLabel(appt.from) }}</div>
                  <div class="date-tile-day text-sm-semibold text-gray-900">{{ getDayLabel(appt.from) }}</div>
                </div>
                <div class="flex flex-column gap-1">
                  <div class="text-sm-medium text-gray-700 white-space-nowrap">{{ formatTimeRange(appt.from, appt.to) }}</div>
                  <div>
                    <p-chip label="Zakazan" class="info sm"></p-chip>
                  </div>
                </div>
              </div>

              <div class="row-actions flex align-items-center">
                @if (!appt.isShortNotice) {
                  <app-button
                    [btnSeverity]="ButtonSeverity.SECONDARY"
                    [btnSize]="ButtonSize.MD"
                    (btnClicked)="openReschedule(appt.id!, $event)"
                  >
                    <app-edit-03 stroke="#181D27" width="16px" height="16px"></app-edit-03>
                    <div class="text-sm-semibold text-gray-900">Izmeni</div>
                  </app-button>
                }
                <app-button
                  [btnSeverity]="ButtonSeverity.SECONDARY"
                  [btnSize]="ButtonSize.MD"
                  (btnClicked)="openCancel(appt.id!, $event)"
                >
                  <app-slash-circle-01 stroke="#181D27" width="16px" height="16px"></app-slash-circle-01>
                </app-button>
              </div>
            </div>
          }
        </div>
      }
    </div>
  </div>
</div>

<app-client-appointment-reschedule-dialog
  [(visible)]="showRescheduleDialog"
  [appointmentId]="selectedRescheduleId()"
  (rescheduled)="onChanged()"
></app-client-appointment-reschedule-dialog>

<app-client-appointment-cancel-dialog
  [(visible)]="showCancelDialog"
  [appointmentId]="selectedCancelId()"
  (cancelled)="onChanged()"
></app-client-appointment-cancel-dialog>

<app-client-appointment-details-drawer
  [(visible)]="showDetailsDrawer"
  [appointmentId]="selectedDetailsId()"
  (cancelled)="onChanged()"
  (rescheduled)="onChanged()"
  (documentUploaded)="onChanged()"
></app-client-appointment-details-drawer>
